import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  LogOut,
  FileText,
  FileSpreadsheet,
  FileBadge,
  ClipboardCheck,
  ClipboardList,
  Award,
  Download,
  Loader2,
  Layers,
  ScrollText,
  RotateCw,
  ShieldCheck,
  FolderUp,
  Receipt,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { adminApi, clearSession, getUsername } from "@/lib/auth";
import { CourseInvoiceDialog } from "@/components/CourseInvoiceDialog";
import { toast } from "sonner";
import { logWarn, logError } from "@/lib/log";

// ---------------------------------------------------------------------------
// Core middle documents (single-button each, same for every CCAA)
// ---------------------------------------------------------------------------
const CORE_DOCS = [
  {
    key: "certificate-issefe",
    title: "Certificado ISSEFE",
    description:
      "Certificado del curso impartido (PDF) con datos del cliente, instructor, horas, fechas y firmas.",
    icon: FileBadge,
    accent: "from-zinc-900 to-zinc-700",
  },
  {
    key: "control-firmas",
    title: "Control de firmas",
    description:
      "Hoja de control de firmas ISSEFE (PDF) con los participantes y su calificación.",
    icon: FileSpreadsheet,
    accent: "from-emerald-700 to-emerald-500",
  },
  {
    key: "ficha-dfmr",
    title: "Ficha de inscripción DFMR",
    description:
      "Ficha de inscripción RGPD por alumno (PDF). Si hay más de un alumno, se descarga un ZIP con todas.",
    icon: FileText,
    accent: "from-indigo-700 to-indigo-500",
  },
  {
    key: "exam",
    title: "Examen corregido",
    description:
      "Examen corregido en PDF con la evaluación (apto/no apto) y todas las preguntas marcadas.",
    icon: ClipboardCheck,
    accent: "from-amber-700 to-amber-500",
  },
  {
    key: "certificates",
    title: "Certificados",
    description:
      "Certificado oficial individual por alumno (PDF firmado digitalmente). Se descargan uno a uno, en archivos separados (sin ZIP).",
    icon: Award,
    accent: "from-rose-700 to-rose-500",
  },
  {
    // Tarjeta especial: abre el diálogo de factura en vez de descargar.
    key: "invoice",
    title: "Factura",
    description:
      "Factura del curso con el cliente, la tarifa de la CCAA, el desplazamiento, las fechas y el nº de alumnos ya rellenos. Se crea directamente en Facturación.",
    icon: Receipt,
    accent: "from-teal-700 to-teal-500",
  },
];

// Encuesta de satisfacción: obligatoria en MURCIA, una hoja por alumno.
const SURVEY_DOC = {
  key: "encuestas",
  title: "Encuesta de satisfacción",
  description:
    "Encuestas de satisfacción en PDF, una hoja por alumno. Las que el alumno rellenó tras el examen salen ya marcadas.",
  icon: ClipboardList,
  accent: "from-sky-700 to-sky-500",
};

export default function DocumentationScreen() {
  const navigate = useNavigate();
  const username = getUsername() || "";
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [downloading, setDownloading] = useState(null);
  const [catalog, setCatalog] = useState({ previa: [], final: [] });
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [cRes, catRes] = await Promise.all([
          adminApi.get("/courses"),
          adminApi.get("/documents/ccaa-catalog"),
        ]);
        setCourses(
          [...(cRes.data || [])].sort((a, b) =>
            String(a?.name || a?.client_name || "").localeCompare(
              String(b?.name || b?.client_name || ""),
              "es",
              { sensitivity: "base" }
            )
          )
        );
        setCatalog(catRes.data || { previa: [], final: [] });
      } catch (e) {
        toast.error("No se pudieron cargar los cursos.");
      } finally {
        setLoading(false);
      }
    })();
    // adminApi/toast son imports estables; setters de useState lo son
    // tambien. El effect debe correr SOLO al montar la pantalla.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedCourse = useMemo(
    () => courses.find((c) => c.id === selectedCourseId),
    [courses, selectedCourseId]
  );

  // La encuesta de satisfacción solo se entrega en MURCIA. Los cursos PPAA
  // no llevan examen ni documentos de ISSEFE.
  const isPpaa = selectedCourse?.course_type === "ppaa";
  const coreDocs = useMemo(() => {
    if (isPpaa) {
      return CORE_DOCS.filter((d) => ["control-firmas", "ficha-dfmr", "certificates", "invoice"].includes(d.key));
    }
    const isMurcia = (selectedCourse?.region_name || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toUpperCase()
      .includes("MURCIA");
    return isMurcia ? [...CORE_DOCS, SURVEY_DOC] : CORE_DOCS;
  }, [selectedCourse, isPpaa]);

  // Normalisador para comparar nombres de CCAA contra códigos del catálogo
  // (los códigos se generan como slug minúsculas/separados por guiones, ej.
  // "castilla-y-leon", "la-rioja").
  const _slug = (s) =>
    (s || "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[\s_]+/g, "-")
      .replace(/[^a-z-]/g, "");

  // Sólo se muestra la tarjeta PREVIA cuando hay un curso seleccionado y
  // únicamente con la CCAA del curso (filtrada del catálogo global).
  const previaItemsForCourse = useMemo(() => {
    if (!selectedCourse || selectedCourse.course_type === "ppaa") return [];
    const courseSlug = _slug(selectedCourse.region_name);
    if (!courseSlug) return [];
    return (catalog.previa || []).filter(
      (it) => it.code === courseSlug
    );
  }, [catalog, selectedCourse]);

  // Análogamente para la documentación FINAL (cliente requirement 2026-06):
  // sólo se muestra el ítem FINAL correspondiente a la CCAA del curso
  // seleccionado.
  const finalItemsForCourse = useMemo(() => {
    if (!selectedCourse || selectedCourse.course_type === "ppaa") return [];
    const courseSlug = _slug(selectedCourse.region_name);
    if (!courseSlug) return [];
    return (catalog.final || []).filter(
      (it) => it.code === courseSlug
    );
  }, [catalog, selectedCourse]);

  const handleLogout = () => {
    clearSession();
    toast.success("Sesión cerrada.");
    navigate("/admin/login");
  };

  // GENERACIÓN Y ENVÍO AUTOMÁTICO: genera toda la documentación del curso y
  // la vuelca en su carpeta de Google Drive. El trabajo corre en el servidor
  // y aquí se consulta su progreso cada pocos segundos.
  const [autoJob, setAutoJob] = useState(null);
  const [autoStarting, setAutoStarting] = useState(false);
  const autoRunning = autoStarting || autoJob?.status === "running";

  useEffect(() => {
    setAutoJob(null);
    if (!selectedCourseId) return;
    adminApi
      .get(`/documents/courses/${selectedCourseId}/auto-generate`)
      .then((res) => setAutoJob(res.data?.id ? res.data : null))
      .catch(() => setAutoJob(null));
  }, [selectedCourseId]);

  useEffect(() => {
    if (autoJob?.status !== "running") return;
    const timer = setInterval(async () => {
      try {
        const res = await adminApi.get(
          `/documents/courses/${autoJob.course_id}/auto-generate`
        );
        if (res.data?.id) setAutoJob(res.data);
        if (res.data?.status === "done") {
          toast.success(
            `Documentación generada: ${res.data.uploaded} archivo(s) en Drive.`
          );
        } else if (res.data?.status === "error") {
          toast.error(res.data.detail || "La generación automática falló.");
        }
      } catch (e) {
        logWarn("No se pudo consultar el progreso", e);
      }
    }, 3000);
    return () => clearInterval(timer);
  }, [autoJob?.status, autoJob?.course_id, autoJob?.id]);

  const handleAutoGenerate = async () => {
    if (!selectedCourseId) {
      toast.error("Selecciona primero un curso.");
      return;
    }
    setAutoStarting(true);
    try {
      const res = await adminApi.post(
        `/documents/courses/${selectedCourseId}/auto-generate`
      );
      setAutoJob(res.data);
      toast.info("Generando la documentación y subiéndola a Drive…");
    } catch (e) {
      logError("Auto-generación fallida", e);
      toast.error(
        e?.response?.data?.detail
          || "No se pudo lanzar la generación automática."
      );
    } finally {
      setAutoStarting(false);
    }
  };

  const downloadFromUrl = async (relativeUrl, busyKey) => {
    if (!selectedCourseId) {
      toast.error("Selecciona primero un curso.");
      return;
    }
    setDownloading(busyKey);
    try {
      const res = await adminApi.get(relativeUrl, { responseType: "blob" });
      const cd =
        res.headers["content-disposition"] || res.headers["Content-Disposition"];
      let filename = `${busyKey}.bin`;
      if (cd) {
        const match = cd.match(/filename="?([^";]+)"?/i);
        if (match) filename = match[1];
      }
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`Descargado: ${filename}`);
    } catch (e) {
      let msg = "No se pudo descargar el documento.";
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const parsed = JSON.parse(text);
          msg = parsed.detail || msg;
        } catch (parseError) {
          // Body wasn't valid JSON — keep the generic message.
          logWarn("Could not parse error blob", parseError);
        }
      } else if (e.response?.data?.detail) {
        msg = e.response.data.detail;
      }
      toast.error(msg);
    } finally {
      setDownloading(null);
    }
  };

  const handleCoreDownload = (docKey) => {
    if (docKey === "certificates") {
      return handleCertificatesIndividual();
    }
    return downloadFromUrl(
      `/documents/courses/${selectedCourseId}/${docKey}`,
      docKey
    );
  };

  // Cliente requirement (2026-06): el botón "Certificados" debe descargar
  // un archivo PDF por alumno (no un ZIP). Iteramos sobre las asistencias
  // del curso y pedimos cada certificado individual al endpoint con
  // `?attendance_id=...`.
  const handleCertificatesIndividual = async () => {
    if (!selectedCourseId) {
      toast.error("Selecciona primero un curso.");
      return;
    }
    setDownloading("certificates");
    try {
      const { data: attendances } = await adminApi.get(
        `/attendance?course_id=${selectedCourseId}`
      );
      if (!Array.isArray(attendances) || attendances.length === 0) {
        toast.error("Este curso no tiene alumnos dados de alta.");
        return;
      }
      const errors = [];
      toast.info(
        `Descargando ${attendances.length} certificados, uno por alumno…`
      );
      let okCount = 0;
      for (let i = 0; i < attendances.length; i += 1) {
        const a = attendances[i];
        try {
          const res = await adminApi.get(
            `/documents/courses/${selectedCourseId}/certificates?attendance_id=${a.id}`,
            { responseType: "blob" }
          );
          const cd =
            res.headers["content-disposition"]
            || res.headers["Content-Disposition"];
          let filename = `${(a.name || "").trim()} ${(a.surname || "").trim()}.pdf`.trim();
          if (cd) {
            const match = cd.match(/filename="?([^";]+)"?/i);
            if (match) filename = match[1];
          }
          const blob = new Blob([res.data], { type: "application/pdf" });
          const blobUrl = window.URL.createObjectURL(blob);
          // En móvil/Safari, abrir cada descarga en una nueva pestaña falla
          // por el bloqueo de pop-ups. Usamos un <a download> dentro del DOM
          // y un setTimeout para dar tiempo al navegador a procesar cada
          // descarga antes de la siguiente.
          const link = document.createElement("a");
          link.href = blobUrl;
          link.download = filename;
          link.rel = "noopener";
          link.style.display = "none";
          document.body.appendChild(link);
          link.click();
          // Limpieza diferida para garantizar que el navegador haya iniciado
          // la descarga ANTES de revocar la URL.
          setTimeout(() => {
            try { link.remove(); } catch { /* ignore */ }
            try { window.URL.revokeObjectURL(blobUrl); } catch { /* ignore */ }
          }, 800);
          okCount += 1;
          // 600 ms entre descargas: Safari móvil necesita un poco más de
          // tiempo que Chrome desktop para no descartar descargas.
          await new Promise((r) => setTimeout(r, 600));
        } catch (e) {
          // Cuando pedimos el certificado con responseType:"blob", axios
          // devuelve el body como Blob incluso en errores 4xx/5xx. Para
          // exponer el "detail" real del backend al usuario, leemos ese
          // blob como texto y lo parseamos si es JSON.
          let detail = "error";
          try {
            if (e?.response?.data instanceof Blob) {
              const text = await e.response.data.text();
              try {
                const parsed = JSON.parse(text);
                detail = parsed?.detail || text.slice(0, 200);
              } catch (_parseErr) {
                detail = text.slice(0, 200) || e?.response?.statusText || "error";
              }
            } else {
              detail =
                e?.response?.data?.detail
                || e?.response?.statusText
                || e?.message
                || "error";
            }
          } catch (_readErr) {
            detail = e?.response?.statusText || e?.message || "error";
          }
          errors.push(`${a.name} ${a.surname} (${a.dni}): ${detail}`);
          logWarn(`Certificado fallido para ${a.dni}`, e);
        }
      }
      if (errors.length === 0) {
        toast.success(`${okCount} certificados descargados.`);
      } else if (okCount === 0) {
        toast.error(
          "Ningún certificado se pudo generar. " + errors.slice(0, 2).join(" | ")
        );
      } else {
        toast.warning(
          `${okCount} OK, ${errors.length} fallidos. ${errors.slice(0, 2).join(" | ")}`
        );
      }
    } catch (e) {
      const msg = e.response?.data?.detail
        || "No se pudieron descargar los certificados.";
      toast.error(msg);
    } finally {
      setDownloading(null);
    }
  };

  const handleCcaaDownload = (kind, code) =>
    downloadFromUrl(
      `/documents/courses/${selectedCourseId}/${kind}/${code}`,
      `${kind}-${code}`
    );

  return (
    <div
      className="min-h-screen w-full bg-[#FAFAFA]"
      data-testid="documentation-screen"
    >
      <header className="border-b border-zinc-200 bg-white sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <button
            onClick={() => navigate("/admin")}
            className="inline-flex items-center gap-2 text-sm font-medium text-zinc-700 hover:text-zinc-950 transition-colors"
            data-testid="docs-back-button"
          >
            <ChevronLeft className="h-4 w-4" />
            Gestión
          </button>
          <div className="flex items-center gap-4">
            <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 hidden sm:block">
              Documentación · {username}
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={handleLogout}
              className="h-9 border border-zinc-300 hover:bg-zinc-50"
              data-testid="docs-logout-button"
            >
              <LogOut className="h-3.5 w-3.5 mr-1.5" />
              Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-10 py-10 md:py-14">
        <div className="mb-8 md:mb-12">
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-zinc-500 mb-3">
            <span className="inline-block h-2 w-2 bg-black" />
            Documentación
          </div>
          <h1 className="heading-font text-4xl sm:text-5xl font-extrabold text-zinc-950 leading-[0.95]">
            Generar documentos del curso
          </h1>
          <p className="mt-3 text-zinc-600">
            Selecciona un curso para generar la documentación previa por CCAA,
            el certificado ISSEFE, el control de firmas, las fichas DFMR, los
            exámenes corregidos y la documentación final por CCAA.
          </p>
        </div>

        {/* Course selector */}
        <div
          className="bg-white border border-zinc-200 rounded-lg p-6 md:p-8 mb-8"
          data-testid="docs-course-selector-card"
        >
          <label className="block text-sm font-semibold text-zinc-800 mb-2 uppercase tracking-[0.1em]">
            Curso
          </label>
          <Select
            value={selectedCourseId}
            onValueChange={setSelectedCourseId}
            disabled={loading}
          >
            <SelectTrigger
              className="h-12 border-2 border-zinc-300 focus:ring-0 focus:border-zinc-900"
              data-testid="docs-course-select"
            >
              <SelectValue
                placeholder={loading ? "Cargando cursos…" : "Selecciona un curso"}
              />
            </SelectTrigger>
            <SelectContent>
              {courses.map((c) => (
                <SelectItem
                  key={c.id}
                  value={c.id}
                  data-testid={`docs-course-option-${c.id}`}
                >
                  {c.name || c.client_name}
                  {c.city ? ` · ${c.city}` : ""}
                  {c.date1 ? ` · ${c.date1}` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {selectedCourse && (
            <div
              className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm"
              data-testid="docs-course-summary"
            >
              <div>
                <div className="text-xs uppercase tracking-[0.12em] text-zinc-500">
                  Cliente
                </div>
                <div className="font-semibold text-zinc-900 truncate">
                  {selectedCourse.client_name || "—"}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.12em] text-zinc-500">
                  CCAA
                </div>
                <div className="font-semibold text-zinc-900 truncate">
                  {selectedCourse.region_name || "—"}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.12em] text-zinc-500">
                  Asistencias
                </div>
                <div className="font-semibold text-zinc-900">
                  {selectedCourse.attendance_count || 0}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.12em] text-zinc-500">
                  Código curso
                </div>
                <div className="font-mono font-semibold text-zinc-900">
                  {selectedCourse.course_sequence && selectedCourse.course_year
                    ? `${String(selectedCourse.course_year).slice(-2)}-${selectedCourse.course_sequence}`
                    : "—"}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* === 1. DOCUMENTACIÓN PREVIA CCAA (sólo la CCAA del curso seleccionado) === */}
        {selectedCourseId && previaItemsForCourse.length > 0 && (
          <CcaaSection
            kind="previa"
            title="Documentación previa CCAA"
            description="Memorias y formularios autonómicos que se entregan ANTES de impartir el curso, específica de la CCAA del curso seleccionado."
            icon={Layers}
            accent="from-sky-700 to-sky-500"
            items={previaItemsForCourse}
            courseSelected={!!selectedCourseId}
            downloading={downloading}
            onDownload={(code) => handleCcaaDownload("previa", code)}
          />
        )}

        {/* === 2. Núcleo (Certificado ISSEFE, Control firmas, Ficha DFMR, Examen, Certificados) === */}
        <div className="mt-8 mb-4">
          <button
            type="button"
            onClick={handleAutoGenerate}
            disabled={autoRunning || !selectedCourseId}
            className="inline-flex items-center gap-2 h-12 px-6 rounded-md bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-50 disabled:cursor-not-allowed font-bold text-sm uppercase tracking-[0.08em] transition-colors shadow-[0_12px_30px_-16px_rgba(0,0,0,0.9)]"
            data-testid="auto-generate-btn"
            title="Genera toda la documentación del curso y la sube a su carpeta de Google Drive"
          >
            {autoRunning ? (
              <>
                <RotateCw className="h-4 w-4 animate-spin" />
                Generando y subiendo…
              </>
            ) : (
              <>
                <FolderUp className="h-4 w-4" />
                Generación y envío automático
              </>
            )}
          </button>
          <p className="mt-2 text-sm text-zinc-600">
            Genera todos los documentos del curso seleccionado y los vuelca en
            su carpeta de Google Drive.
          </p>
        </div>

        {autoJob && (
          <div
            className={`mb-6 border-2 rounded-lg p-5 md:p-6 ${
              autoJob.status === "running"
                ? "bg-zinc-50 border-zinc-900"
                : autoJob.failed > 0 || autoJob.status === "error"
                  ? "bg-rose-50 border-rose-500"
                  : "bg-emerald-50 border-emerald-500"
            }`}
            data-testid="auto-generate-panel"
          >
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] font-bold text-zinc-600 mb-1">
                  {autoJob.status === "running"
                    ? `Generando… ${autoJob.current || ""}`
                    : autoJob.status === "error"
                      ? "La generación se interrumpió"
                      : "Documentación volcada en Drive"}
                </div>
                <h3 className="heading-font text-lg font-extrabold text-zinc-950">
                  {autoJob.folder_name || autoJob.course_name}
                </h3>
                <div className="text-sm text-zinc-700 mt-1">
                  {autoJob.uploaded || 0} archivo(s) subidos
                  {autoJob.failed ? ` · ${autoJob.failed} con error` : ""}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {autoJob.folder_url && (
                  <a
                    href={autoJob.folder_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-md border-2 border-zinc-900 text-zinc-900 hover:bg-zinc-100 text-sm font-semibold transition-colors"
                    data-testid="auto-drive-link"
                  >
                    <FolderUp className="h-4 w-4" />
                    Abrir carpeta
                  </a>
                )}
                {autoJob.status !== "running" && (
                  <button
                    type="button"
                    onClick={() => setAutoJob(null)}
                    className="text-zinc-500 hover:text-zinc-900 text-xs uppercase tracking-wide font-semibold"
                    data-testid="auto-generate-close"
                  >
                    Ocultar
                  </button>
                )}
              </div>
            </div>
            <ul className="space-y-2">
              {(autoJob.steps || []).map((st) => (
                <li
                  key={st.key}
                  className="flex items-start gap-3 text-sm bg-white border border-zinc-200 rounded-md px-3 py-2"
                  data-testid={`auto-step-${st.key}`}
                >
                  <span className="mt-0.5 shrink-0">
                    {st.status === "ok" ? (
                      <ShieldCheck className="h-4 w-4 text-emerald-600" />
                    ) : st.status === "running" ? (
                      <RotateCw className="h-4 w-4 text-zinc-900 animate-spin" />
                    ) : st.status === "error" ? (
                      <span className="text-rose-700 font-bold">✗</span>
                    ) : st.status === "skipped" ? (
                      <span className="text-amber-700 font-bold">⚠</span>
                    ) : (
                      <span className="text-zinc-400">•</span>
                    )}
                  </span>
                  <span className="min-w-0">
                    <span className="font-semibold text-zinc-900">
                      {st.label}
                    </span>
                    {st.detail && (
                      <span
                        className={`block text-xs mt-0.5 ${
                          st.status === "error"
                            ? "text-rose-700"
                            : st.status === "skipped"
                              ? "text-amber-700"
                              : "text-zinc-500"
                        }`}
                      >
                        {st.detail}
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {coreDocs.map((d) => {
            const Icon = d.icon;
            const isInvoice = d.key === "invoice";
            const isDownloading = downloading === d.key;
            return (
              <div
                key={d.key}
                className="relative bg-white border border-zinc-200 rounded-lg p-6 flex flex-col"
                data-testid={`doc-card-${d.key}`}
              >
                <div
                  className={`absolute left-0 top-0 w-1 h-full bg-gradient-to-b ${d.accent}`}
                />
                <div className="flex items-start gap-3 mb-3">
                  <div className="h-10 w-10 rounded-md bg-zinc-100 inline-flex items-center justify-center">
                    <Icon className="h-5 w-5 text-zinc-800" />
                  </div>
                  <div>
                    <h3 className="heading-font text-lg font-bold text-zinc-950">
                      {d.title}
                    </h3>
                    <p className="text-sm text-zinc-600 mt-1">
                      {d.description}
                    </p>
                  </div>
                </div>
                <div className="mt-auto pt-3">
                  <Button
                    onClick={() =>
                      isInvoice
                        ? setInvoiceDialogOpen(true)
                        : handleCoreDownload(d.key)
                    }
                    disabled={!selectedCourseId || isDownloading}
                    className="w-full h-11 bg-zinc-950 hover:bg-zinc-800 text-white"
                    data-testid={
                      isInvoice ? "doc-invoice-button" : `doc-download-${d.key}`
                    }
                  >
                    {isDownloading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Generando…
                      </>
                    ) : isInvoice ? (
                      <>
                        <Receipt className="h-4 w-4 mr-2" />
                        Generar factura
                      </>
                    ) : (
                      <>
                        <Download className="h-4 w-4 mr-2" />
                        Descargar
                      </>
                    )}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        {/* === 3. DOCUMENTACIÓN FINAL CCAA (sólo la CCAA del curso) === */}        {selectedCourseId && finalItemsForCourse.length > 0 && (
          <div className="mt-8">
            <CcaaSection
              kind="final"
              title="Documentación final CCAA"
              description="Memorias finales autonómicas que se entregan DESPUÉS del curso, específica de la CCAA del curso seleccionado."
              icon={ScrollText}
              accent="from-purple-700 to-purple-500"
              items={finalItemsForCourse}
              courseSelected={!!selectedCourseId}
              downloading={downloading}
              onDownload={(code) => handleCcaaDownload("final", code)}
            />
          </div>
        )}

        {/* === 4. (Factura y encuesta van en la rejilla de arriba) === */}
      </main>

      <CourseInvoiceDialog
        courseId={selectedCourseId}
        open={invoiceDialogOpen}
        onOpenChange={setInvoiceDialogOpen}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card that lists the CCAA buttons for a given `kind` (previa | final).
// Disabled CCAAs are greyed out + tooltip "Plantilla pendiente".
// ---------------------------------------------------------------------------
function CcaaSection({
  kind,
  title,
  description,
  icon: Icon,
  accent,
  items,
  courseSelected,
  downloading,
  onDownload,
}) {
  return (
    <div
      className="relative bg-white border border-zinc-200 rounded-lg p-6"
      data-testid={`ccaa-card-${kind}`}
    >
      <div
        className={`absolute left-0 top-0 w-1 h-full bg-gradient-to-b ${accent}`}
      />
      <div className="flex items-start gap-3 mb-4">
        <div className="h-10 w-10 rounded-md bg-zinc-100 inline-flex items-center justify-center">
          <Icon className="h-5 w-5 text-zinc-800" />
        </div>
        <div>
          <h3 className="heading-font text-lg font-bold text-zinc-950">
            {title}
          </h3>
          <p className="text-sm text-zinc-600 mt-1">{description}</p>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="text-sm text-zinc-500 py-4 text-center border border-dashed border-zinc-200 rounded-md">
          Sin CCAA configuradas.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
          {items.map((it) => {
            const busyKey = `${kind}-${it.code}`;
            const isDownloading = downloading === busyKey;
            const disabled = !it.enabled || !courseSelected || isDownloading;
            return (
              <Button
                key={it.code}
                onClick={() => onDownload(it.code)}
                disabled={disabled}
                className={`h-11 text-sm font-semibold rounded-md justify-start ${
                  it.enabled
                    ? "bg-zinc-950 hover:bg-zinc-800 text-white disabled:bg-zinc-200 disabled:text-zinc-500"
                    : "bg-zinc-100 text-zinc-400 cursor-not-allowed hover:bg-zinc-100"
                }`}
                title={
                  !it.enabled
                    ? "Plantilla pendiente — adjunta el .docx al agente"
                    : !courseSelected
                    ? "Selecciona un curso primero"
                    : it.label
                }
                data-testid={`ccaa-${kind}-${it.code}`}
              >
                {isDownloading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                <span className="truncate">{it.label}</span>
                {!it.enabled && (
                  <span className="ml-auto text-[10px] uppercase tracking-wider bg-zinc-200 text-zinc-500 px-1.5 py-0.5 rounded">
                    Pendiente
                  </span>
                )}
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
}
